import type { FieldComponent } from '@atlas/react-forms';
import classNames from 'classnames';
import {
  type InputHTMLAttributes,
  type ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import PhoneInput, {
  type Value,
  isValidPhoneNumber,
} from 'react-phone-number-input';

import Errors from 'components/Form/Errors';
import Icon from 'components/Icon/Icon';
import Tooltip from 'components/Tooltip';
import { getFlagPath } from 'utils/assets';
import pushToDataLayer, { btag } from 'utils/dataLayer';
import { useTranslation } from 'utils/translations';

import styles from './PhoneField.module.scss';

const Input = forwardRef<HTMLInputElement>(
  (props: InputHTMLAttributes<HTMLInputElement>, ref) => {
    const lockedFieldHint = useTranslation('locked_field_hint');

    return (
      <>
        <input {...props} className={styles.input} ref={ref} />
        {props.disabled && (
          <div className={styles['icon-box']}>
            <Tooltip message={lockedFieldHint}>
              <Icon className={styles.icon} name="lock" />
            </Tooltip>
          </div>
        )}
      </>
    );
  },
);

const PhoneField: FieldComponent = ({ onChange, onFocus, onBlur, field }) => {
  const placeholder = useTranslation(field.label.toLowerCase());
  const [showError, setShowError] = useState(false);
  const [focus, setFocus] = useState<boolean>(false);
  const refElement = useRef<HTMLDivElement>(null);

  const isValidField = !field.errors.length;

  const handleChangeValue = useCallback(
    (value?: Value) => {
      onChange?.(value, true);

      if (value && isValidPhoneNumber(value)) {
        pushToDataLayer('phone_filled', btag);
      }
    },
    [onChange],
  );

  const renderCountrySelect = useCallback<
    ({ value }: { value: Value }) => ReactNode
  >(({ value }) => {
    if (!value) return null;

    return (
      <div className={styles['country-info']}>
        <img className={styles.flag} src={getFlagPath(value)} alt={value} />
      </div>
    );
  }, []);

  const handleBlur = () => {
    onBlur?.();
    setShowError(true);
    setFocus(false);
  };

  const handleFocus = () => {
    onFocus?.();
    setShowError(false);
    setFocus(true);
  };

  const fieldValue = typeof field.value === 'string' ? field.value : '';

  const placeholderText = `${placeholder}${field.isRequired ? ' *' : ''}`;

  const focusInput = useCallback(() => {
    refElement?.current?.querySelector('input')?.focus();
  }, []);

  useEffect(() => {
    if (!field.errors.length) {
      pushToDataLayer('phone_filled', btag);
    }
  }, [field.errors]);

  return (
    <div
      className={styles['form-field-container']}
      ref={refElement}
      onClick={focusInput}
      onKeyDown={focusInput}
    >
      <div className={styles['field-inner']}>
        <PhoneInput
          className={classNames(styles['phone-field'], {
            [styles.error]: !isValidField && showError,
            [styles.disabled]: !field.isModifiable || field.disabled,
          })}
          name={field.name}
          id={field.name}
          value={fieldValue}
          inputComponent={Input}
          onChange={handleChangeValue}
          onBlur={handleBlur}
          disabled={field.disabled || !field.isModifiable}
          onFocus={handleFocus}
          countrySelectComponent={renderCountrySelect}
          international
          smartCaret={false}
        />
        {placeholder && (
          <label
            tabIndex={-1}
            htmlFor={field.name}
            onClick={handleFocus}
            onKeyDown={handleFocus}
            className={classNames(styles.placeholder, {
              [styles.focus]: focus || !!fieldValue,
            })}
          >
            {placeholderText}
          </label>
        )}
      </div>
      {showError && <Errors errors={field.errors} />}
    </div>
  );
};

export default PhoneField;
